/**
 * The worker agent: a single executable people install on their own machines
 * instead of running the worker in Docker (apps/worker/src/agent). The web app
 * shows a setup code; the installer turns it into the agent's configuration.
 */

export interface AgentSetup {
  /** The vidmakr API the worker talks to (its public URL). */
  apiUrl: string;
  /** The worker's token (WORKER_TOKEN). */
  token: string;
  /** Name the worker shows up under. */
  name: string;
}

const PREFIX = 'vmw1.';

function toBase64Url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): string {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
}

/** One copyable string with everything the agent needs (it contains the token: show it once). */
export function encodeSetupCode(s: AgentSetup): string {
  return PREFIX + toBase64Url(JSON.stringify({ u: s.apiUrl, t: s.token, n: s.name }));
}

export function decodeSetupCode(code: string): AgentSetup {
  const c = code.trim();
  if (!c.startsWith(PREFIX)) throw new Error('This is not a vidmakr worker setup code');
  let raw: { u?: unknown; t?: unknown; n?: unknown };
  try {
    raw = JSON.parse(fromBase64Url(c.slice(PREFIX.length)));
  } catch {
    throw new Error('The setup code is damaged; copy it again');
  }
  if (typeof raw.u !== 'string' || !/^https?:\/\//.test(raw.u) || typeof raw.t !== 'string' || !raw.t) throw new Error('The setup code is incomplete; copy it again');
  return { apiUrl: raw.u.replace(/\/+$/, ''), token: raw.t, name: typeof raw.n === 'string' ? raw.n : '' };
}

/** Platforms the agent is built for, and the file each is published as. */
export const AGENT_PLATFORMS = [
  { id: 'windows-x64', label: 'Windows (x64)', file: 'vidmakr-worker-windows-x64.exe' },
  { id: 'linux-x64', label: 'Linux (x64)', file: 'vidmakr-worker-linux-x64' },
  { id: 'darwin-arm64', label: 'macOS (Apple silicon, CPU only)', file: 'vidmakr-worker-darwin-arm64' },
] as const;

export type AgentPlatformId = (typeof AGENT_PLATFORMS)[number]['id'];

/** Files served under /downloads/worker/ (the binaries, the install scripts and the published version). */
export const AGENT_DOWNLOADS = [...AGENT_PLATFORMS.map((p) => p.file), 'version.txt'];

/** Install commands for each OS, given the API's public URL and a setup code. */
export function agentInstallCommands(apiUrl: string, code: string) {
  const base = `${apiUrl.replace(/\/+$/, '')}/downloads/worker`;
  return {
    windows: `& ([scriptblock]::Create((irm '${base}/install.ps1'))) '${code}'`,
    linux: `curl -fsSL ${base}/install.sh | sudo sh -s -- '${code}'`,
    macos: `curl -fsSL ${base}/install.sh | sudo sh -s -- '${code}'`,
  };
}
